import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Users, Baby, UserCheck, Clock, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const field = "w-full bg-[#F0E8D9] rounded-xl px-3 py-2 text-sm text-[#493329] outline-none focus:ring-2 focus:ring-[#C99A55]/40";
const label = "text-xs font-semibold text-[#493329] mb-1 block";

export default function EventAdminPanel() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/event`, { withCredentials: true })
    .then((r) => { setData(r.data); setCfg(r.data.config); })
    .catch(() => setData({ stats: {}, confirmed: [], waitlist: [] }));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/event/config`, cfg, { withCredentials: true });
      setCfg(r.data);
      toast.success('Événement mis à jour.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement.');
    } finally { setSaving(false); }
  };

  const removeReg = async (id, name) => {
    if (!window.confirm(`Retirer ${name} de l'événement ?`)) return;
    try { await axios.delete(`${API}/admin/event/register/${id}`, { withCredentials: true }); toast.success('Inscription retirée.'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const setC = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  if (!data || !cfg) {
    return <Card className="bg-white border-[#E9DDCC] rounded-2xl"><CardContent className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></CardContent></Card>;
  }

  const s = data.stats;
  const kpis = [
    { icon: UserCheck, label: 'Clientes confirmées', value: `${s.confirmed}/${s.capacity}` },
    { icon: Clock, label: 'Liste d\'attente', value: s.waitlist },
    { icon: Users, label: 'Accompagnateurs', value: s.total_guests },
    { icon: Baby, label: 'Enfants', value: s.total_children },
    { icon: Users, label: 'Total personnes', value: s.total_heads },
  ];

  return (
    <Card className="bg-white border-[#E9DDCC] rounded-2xl" data-testid="event-admin-panel">
      <CardHeader>
        <CardTitle className="font-heading text-xl text-[#493329]">Inauguration — gestion</CardTitle>
        <p className="text-xs text-[#7A6455]">Inscriptions des clientes, liste d'attente et contenu de la page événement.</p>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {kpis.map((k, i) => (
            <div key={i} className="bg-[#F0E8D9] rounded-xl px-3 py-3 text-center" data-testid={`event-kpi-${i}`}>
              <k.icon className="w-4 h-4 text-[#C99A55] mx-auto mb-1" />
              <div className="text-lg font-semibold text-[#493329]">{k.value}</div>
              <div className="text-[10px] text-[#7A6455] leading-tight">{k.label}</div>
            </div>
          ))}
        </div>

        {/* REGISTRATIONS */}
        <div>
          <h3 className="text-sm font-semibold text-[#493329] mb-3">Clientes confirmées ({data.confirmed.length})</h3>
          <div className="space-y-2">
            {data.confirmed.length === 0 && <p className="text-xs text-[#7A6455]">Aucune inscription pour le moment.</p>}
            {data.confirmed.map((r) => (
              <div key={r.id} className="flex items-center gap-3 bg-[#F5EFE5] border border-[#E9DDCC] rounded-xl px-3 py-2.5" data-testid={`event-reg-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#493329] font-medium truncate">{r.name}</p>
                  <p className="text-xs text-[#7A6455] truncate">{r.email} · +{r.guests_count || 0} adulte(s), {r.children_count || 0} enfant(s){r.guests_names ? ` · ${r.guests_names}` : ''}</p>
                </div>
                <button onClick={() => removeReg(r.id, r.name)} data-testid={`event-reg-del-${r.id}`} className="text-[#C76542] hover:bg-[#C76542]/10 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {data.waitlist.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-[#493329] mt-5 mb-3">Liste d'attente ({data.waitlist.length})</h3>
              <div className="space-y-2">
                {data.waitlist.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-[#C76542]/5 border border-[#C76542]/20 rounded-xl px-3 py-2.5" data-testid={`event-wl-${r.id}`}>
                    <span className="w-7 h-7 rounded-full bg-[#C76542]/15 text-[#C76542] text-xs flex items-center justify-center font-semibold flex-shrink-0">{r.position}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#493329] font-medium truncate">{r.name}</p>
                      <p className="text-xs text-[#7A6455] truncate">{r.email} · +{r.guests_count || 0} adulte(s), {r.children_count || 0} enfant(s)</p>
                    </div>
                    <button onClick={() => removeReg(r.id, r.name)} data-testid={`event-wl-del-${r.id}`} className="text-[#C76542] hover:bg-[#C76542]/10 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* CONFIG EDITOR */}
        <div className="border-t border-[#E9DDCC] pt-6">
          <h3 className="text-sm font-semibold text-[#493329] mb-1">Paramètres de la page</h3>
          <p className="text-xs text-[#7A6455] mb-4">La mise en page et les textes de présentation sont figés (direction artistique). Vous pilotez ici l'ouverture, la date et la capacité.</p>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-[#493329]" data-testid="event-enabled">
                <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setC('enabled', e.target.checked)} /> Page visible
              </label>
              <label className="flex items-center gap-2 text-sm text-[#493329]" data-testid="event-regopen">
                <input type="checkbox" checked={!!cfg.registration_open} onChange={(e) => setC('registration_open', e.target.checked)} /> Inscriptions ouvertes
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><span className={label}>Date (texte affiché)</span><input className={field} data-testid="event-cfg-datelabel" value={cfg.date_label || ''} onChange={(e) => setC('date_label', e.target.value)} /></div>
              <div><span className={label}>Date précise (optionnel · décompte)</span><input type="date" className={field} data-testid="event-cfg-date" value={cfg.event_date || ''} onChange={(e) => setC('event_date', e.target.value)} /></div>
              <div><span className={label}>Lieu</span><input className={field} data-testid="event-cfg-location" value={cfg.location || ''} onChange={(e) => setC('location', e.target.value)} /></div>
              <div><span className={label}>Capacité (clientes)</span><input type="number" min={1} className={field} data-testid="event-cfg-capacity" value={cfg.capacity || 25} onChange={(e) => setC('capacity', parseInt(e.target.value || '25', 10))} /></div>
            </div>

            <button onClick={save} disabled={saving} data-testid="event-cfg-save"
              className="inline-flex items-center gap-2 bg-[#493329] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#3a2920] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
